import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * One month of an organization's generation activity.
 *
 * Not a `MongoEntity` — like `GenerationTask`, this is a computed answer rather than a record. Nothing
 * stores a month; it is counted out of the generation logs each time the card is opened.
 */
@Entity()
export class GenerationMonthStats extends AdornisEntity {
  static override _class = 'GenerationMonthStats';

  constructor(doc: PartialEntityData<GenerationMonthStats>) {
    super(doc);
  }

  /**
   * Identity, because the client's `EntityStore` refuses to merge an array of entities that has none —
   * without it every refetch would be a fresh set of objects it cannot reconcile with the ones on screen.
   *
   * `organizationId:month` rather than the month alone: the store is keyed per class, so two organizations
   * open in the same session would otherwise share — and merge — a single `2026-07` bucket. Built by the
   * resolver, which is the only thing that knows both halves. See `generationMonthId`.
   */
  @Field(() => String)
  _id!: string;

  /** `YYYY-MM`, in UTC. See `generationMonths.ts` for why that matters. */
  @Field(() => String)
  month: Maybe<string>;

  /** Every run that finished in this month, however it ended. */
  @Field(() => Int)
  total: Maybe<number>;

  @Field(() => Int)
  done: Maybe<number>;

  @Field(() => Int)
  failed: Maybe<number>;
}

/**
 * What the activity card on an organization's ERM detail draws.
 *
 * `months` is the requested window, oldest first and complete — a month nobody generated in is a zero,
 * not a missing entry, because the graph reads the array positionally.
 *
 * The tiles for "this month" and "the window" are summed from `months` on the client rather than sent as
 * their own fields: a number beside a graph it disagrees with is worse than one the reader has to add up.
 * The all-time pair is here because it genuinely cannot be derived from a bounded window.
 */
@Entity()
export class OrganizationGenerationStats extends AdornisEntity {
  static override _class = 'OrganizationGenerationStats';

  constructor(doc: PartialEntityData<OrganizationGenerationStats>) {
    super(doc);
  }

  @Field(() => [GenerationMonthStats], { default: v => v ?? [] })
  months: Maybe<GenerationMonthStats[]>;

  /**
   * Every run ever attributed to this organization. Runs from before the log started recording an
   * organization are in none of these numbers — there is nothing left to attribute them by.
   */
  @Field(() => Int)
  totalAllTime: Maybe<number>;

  @Field(() => Int)
  failedAllTime: Maybe<number>;
}
